import React, { useRef, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import {
  BULK_CREATE_ASSET_IMAGES_MUTATION,
  CREATE_IMAGE_MUTATION,
} from "../../../constants/graphql/mutations";

const CONCURRENCY_LIMIT = 3;

export default function BulkImageUploadModal({ selectedIds = [], onClose, onComplete }) {
  const { notifySuccess, notifyError } = useNotification();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [setAsDefault, setSetAsDefault] = useState(true);

  const [bulkCreateImages] = useMutation(BULK_CREATE_ASSET_IMAGES_MUTATION);
  const [createImage] = useMutation(CREATE_IMAGE_MUTATION);

  const handleFileSelect = (event) => {
    const selected = event.target.files[0];
    if (!selected) return;

    setFile(selected);
    setPreview(URL.createObjectURL(selected));
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!file || selectedIds.length === 0) return;

    setUploading(true);
    setProgress({ current: 0, total: selectedIds.length });

    try {
      // Step 1: Upload image to the first asset via bulkCreateAssetImages.
      // This does the actual Filestack upload and returns the URL.
      const firstAssetId = selectedIds[0];
      let filestackUrl = null;

      await bulkCreateImages({
        variables: { assetId: firstAssetId, images: [file], default: setAsDefault },
        onSuccess: ({ images }) => {
          if (images && images.length > 0) {
            filestackUrl = images[0].url;
          }
        },
        onFailure: (errors) => {
          throw new Error(errors?.submit || "Failed to upload image");
        },
      });

      if (!filestackUrl) {
        throw new Error("No image URL returned from upload");
      }

      setProgress({ current: 1, total: selectedIds.length });

      // Step 2: Create image records for remaining assets using the Filestack URL.
      const remainingIds = selectedIds.slice(1);

      if (remainingIds.length > 0) {
        let completed = 1;
        const executing = new Set();

        for (const assetId of remainingIds) {
          const promise = createImage({
            variables: { assetId, url: filestackUrl, default: setAsDefault },
          })
            .then(() => {
              completed++;
              setProgress({ current: completed, total: selectedIds.length });
            })
            .catch((err) => {
              console.error(`Error creating image for asset ${assetId}:`, err);
              completed++;
              setProgress({ current: completed, total: selectedIds.length });
            })
            .finally(() => executing.delete(promise));

          executing.add(promise);

          if (executing.size >= CONCURRENCY_LIMIT) {
            await Promise.race(executing);
          }
        }

        await Promise.all(executing);
      }

      notifySuccess(
        `Image uploaded to ${selectedIds.length} item${selectedIds.length > 1 ? "s" : ""}`
      );
      onComplete();
    } catch (error) {
      console.error("Bulk image upload error:", error);
      notifyError("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
      setProgress(null);
      if (preview) URL.revokeObjectURL(preview);
    }
  };

  const progressPercent =
    progress ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Dialog open onClose={uploading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6, position: "relative" }}>
        <Typography fontSize={22} fontWeight={500} color="text.primary" align="center">
          Bulk Upload Image
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={uploading}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          This tool allows you to upload the same image to all the selected items.
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        {!file ? (
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: "2px dashed",
              borderColor: "grey.300",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main", bgcolor: "grey.50" },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Click to select an image
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center" }}>
            <Box
              component="img"
              src={preview}
              alt="Preview"
              sx={{
                maxWidth: "100%",
                maxHeight: 200,
                borderRadius: 1,
                objectFit: "contain",
                mb: 1,
              }}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              {file.name}
            </Typography>
            {!uploading && (
              <Button size="small" onClick={handleRemoveFile} sx={{ mt: 0.5 }}>
                Remove
              </Button>
            )}
          </Box>
        )}

        {file && !uploading && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={setAsDefault}
                  onChange={(e) => setSetAsDefault(e.target.checked)}
                  color="primary"
                />
              }
              label="Set as default image for all selected items"
            />
          </Box>
        )}

        {uploading && progress && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progressPercent} />
            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 0.5 }}>
              {progress.current} / {progress.total} items
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!file || uploading}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
